import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Spin, Icon } from 'antd';
import SubscriptionChart from '../../../components/SubscriptionChart';
import AvailableKeysModal from './AvailableKeysModal';
import {
  brokerageSummarySelector,
  loadingBrokerageSelector,
  loadingBrokerageSubscriptionsSelector,
  availableKeysSelector,
} from '../../../../Broker/BrokerSelectors';
import '../BrokerageSubscriptions.scss';
import { isPurchaseEnabled } from '../../../../../../../utils/featureGates';
import { getPartnerContactEmail } from '../../../../../../../themes/partnerTheme';
import HelpTip from './HelpTip';
import { PURCHASE_HELP, SUMMARY_HELP } from '../brokerageSubscriptionHelp';

const Summary = ({
  brokerageSummary = {},
  availableKeys = [],
  loading,
  loadingSubscriptions,
}) => {
  const [summary, setSummary] = useState({
    total: 0,
    assigned: 0,
    expired: 0,
    remaining: 0,
    unAssigned: 0,
    promoCodes: 0,
    unUsedPromoCodes: 0,
    expiredPromoCodes: 0,
    revokedUnUsedPromoCodes: 0,
  });
  const [availableKeysModalVisible, setAvailableKeysModalVisible] = useState(false);

  useEffect(() => {
    if (brokerageSummary) {
      setSummary((s) => ({ ...s, ...brokerageSummary }));
    }
  }, [brokerageSummary]);

  const remainingTotal = summary.remaining;

  const legendData = [
    { name: 'Assigned', value: summary.assigned, color: '#00685e' },
    { name: 'Remaining', value: remainingTotal, color: '#353679' },
    { name: 'Expired', value: summary.expired, color: '#c2410c' },
  ];

  const statCards = [
    { label: 'Total', value: summary.total, mod: 'yellow' },
    { label: 'Assigned', hint: '(Including Promo Codes)', value: summary.assigned, mod: 'teal' },
    { label: 'Expired', hint: '(Including Promo Codes)', value: summary.expired, mod: 'orange' },
    { label: 'Remaining', hint: '(Including Promo Codes)', value: summary.remaining, mod: 'cream' },
    {
      label: 'Unused Subscriptions',
      hint: '(Excluding Promo codes)',
      value: summary.unAssigned,
      mod: 'navy',
      hasViewKeys: true,
    },
    { label: 'Promo codes', value: summary.promoCodes, mod: 'white' },
    { label: 'Unused Promo codes', value: summary.unUsedPromoCodes, mod: 'white' },
    { label: 'Expired Promo Codes', value: summary.expiredPromoCodes, mod: 'orange' },
    {
      label: 'Revoked Unused Promo Codes',
      value: summary.revokedUnUsedPromoCodes,
      mod: 'red',
    },
  ];

  const busy = loading || loadingSubscriptions;
  const canViewKeys = summary.unAssigned > 0 || availableKeys.length > 0;

  return (
    <Spin spinning={busy} wrapperClassName="bsub-summary-spin">
      <div className="bsub-top">
        <div className="bsub-summary-card">
          <h2 className="bsub-summary-title">SUBSCRIPTIONS SUMMARY</h2>
          <div className="bsub-chart-wrap">
            <SubscriptionChart
              summary={{ total: summary.total, remaining: remainingTotal }}
              slices={legendData}
              centerLines={{
                line1: String(remainingTotal),
                line2: `of ${summary.total}`,
              }}
              height={168}
            />
          </div>
          <div className="bsub-legend">
            {legendData.map((item) => (
              <div key={item.name} className="bsub-legend-item">
                <span className="bsub-legend-dot" style={{ background: item.color }} />
                <span className="bsub-legend-label">{item.name}</span>
                <span className="bsub-legend-val">{item.value}</span>
              </div>
            ))}
          </div>
          <div className="bsub-actions">
            {isPurchaseEnabled() ? (
              <div className="bsub-action-row">
                <a href="/purchase" className="bsub-action-btn">
                  <Icon type="plus" />
                  Purchase More Licenses
                </a>
                <HelpTip title={PURCHASE_HELP} placement="bottom" />
              </div>
            ) : (
              <a href="/contact-us" className="bsub-action-btn">
                <Icon type="plus" />
                Purchase More Licenses
              </a>
            )}
            <a href={`mailto:${getPartnerContactEmail()}`} className="bsub-action-btn">
              <Icon type="mail" />
              Email CC Team
            </a>
          </div>
        </div>

        <div className="bsub-stat-grid bsub-stat-grid--summary">
          {statCards.map((card) => (
            <div key={card.label} className={`bsub-stat-card bsub-stat-card--${card.mod}`}>
              <div className="bsub-stat-label">
                <span>{card.label}</span>
                {SUMMARY_HELP[card.label] ? (
                  <HelpTip title={SUMMARY_HELP[card.label]} placement="bottom" />
                ) : null}
              </div>
              <div className="bsub-stat-value">{card.value}</div>
              <div className="bsub-stat-hint">{card.hint || '\u00a0'}</div>
              <div className="bsub-stat-footer">
                {card.hasViewKeys ? (
                  <button
                    type="button"
                    className="bsub-stat-view-keys"
                    disabled={!canViewKeys || busy}
                    onClick={() => setAvailableKeysModalVisible(true)}
                  >
                    <Icon type="key" />
                    View keys
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </div>

      <AvailableKeysModal
        visible={availableKeysModalVisible}
        onCancel={() => setAvailableKeysModalVisible(false)}
        availableKeys={availableKeys}
      />
    </Spin>
  );
};

const mapStateToProps = (state) => ({
  brokerageSummary: brokerageSummarySelector(state),
  availableKeys: availableKeysSelector(state),
  loading: loadingBrokerageSelector(state),
  loadingSubscriptions: loadingBrokerageSubscriptionsSelector(state),
});

export default connect(mapStateToProps)(Summary);
